import React, { useContext, useEffect } from "react";
import BreakpointContext from "../../contexts/breakpointContext";
import DraftDispatchContext from "../../contexts/draftDispatchContext";
import DraftStateContext from "../../contexts/draftStateContext";
import useDraftPicks from "../../hooks/queryResults/draft/useDraftPicks";
import useAllPlayers from "../../hooks/queryResults/useAllPlayers";
import useDraftMutations from "../../hooks/queryResults/draft/useDraftMutations";
import useIsLoading from "../../hooks/queryResults/useIsLoading";
import useSiteRankings from "../../hooks/queryResults/useSiteRankings";
import useTeams from "../../hooks/queryResults/useTeams";
import useUserTeam from "../../hooks/queryResults/useUserTeam";
import useCurrentUser from "../../hooks/useCurrentUser";
import Util from "../../util/util";
import Loading from "../ui/Loading";
import { DRAFT_ACTIONS } from "./draftReducer";
import PlayerCard from "./PlayerCard";

export default function SelectedPlayer() {
  const breakpoints = useContext(BreakpointContext);
  const draftState = useContext(DraftStateContext);
  const draftDispatch = useContext(DraftDispatchContext);
  const currentUser = useCurrentUser();
  const { draftPicksData, draftPicksQuery } = useDraftPicks();
  const { playersData, playersQuery } = useAllPlayers();
  const { siteRankingsData, siteRankingsQuery } = useSiteRankings();
  const { teams, teamsQuery } = useTeams();
  const userTeam = useUserTeam();
  const { pickPlayerMutation } = useDraftMutations();

  const isLoading = useIsLoading(
    draftPicksData,
    draftPicksQuery,
    playersData,
    playersQuery,
    siteRankingsData,
    siteRankingsQuery,
    teams,
    teamsQuery,
    userTeam
  );

  const { selectedPlayerId, watchList } = draftState;
  const {
    hasStarted,
    pickNumOnClock,
    teamIdOnClock,
    timeIsRunningOut,
    hasEnded,
  } = draftState.info;

  let playerId = null;
  let selectedPlayerIsTaken = null;

  const isTaken = (playerId) => {
    return draftPicksData.players.hasOwnProperty(playerId);
  };

  if (!isLoading) {
    selectedPlayerIsTaken = selectedPlayerId && isTaken(selectedPlayerId);

    if (!selectedPlayerId || selectedPlayerIsTaken) {
      if (watchList.length) {
        const availablePlayerIds = watchList.filter(
          (playerId) => !isTaken(playerId)
        );
        if (availablePlayerIds) playerId = availablePlayerIds[0];
      }

      if (playerId) {
        for (const rank in siteRankingsData.playerList.entries) {
          if (siteRankingsData.playerList.entries.hasOwnProperty(rank)) {
            const tempPlayerId = siteRankingsData.playerList.entries[rank];
            if (!isTaken(tempPlayerId)) {
              playerId = tempPlayerId;
              break;
            }
          }
        }
      }
    }
  }

  useEffect(() => {
    if (playerId)
      draftDispatch({
        type: DRAFT_ACTIONS.SELECT_PLAYER_ID,
        payload: playerId,
      });
  }, [draftDispatch, playerId]);

  if (isLoading) return <Loading />;

  const teamOnClock = teamIdOnClock ? teams.getTeamById(teamIdOnClock) : null;
  const userHasDraftManagerRole = Util.hasDraftManagerRole(
    currentUser,
    teamOnClock
  );

  const handleDraftPlayer = () => {
    const data = { pickNum: pickNumOnClock, player: selectedPlayerId };
    pickPlayerMutation.mutate(data);
  };

  const canDraft =
    hasStarted &&
    !hasEnded &&
    (currentUser.isAdmin() || userHasDraftManagerRole);
  const button = canDraft ? (
    <button onClick={handleDraftPlayer} className={"btn"}>
      Draft
    </button>
  ) : (
    <div />
  );

  let playerInfo = null;
  if (selectedPlayerId && playersData) {
    const player = playersData.players[selectedPlayerId];
    let backgroundColor = "#0078f8";
    if (userHasDraftManagerRole) {
      backgroundColor = timeIsRunningOut ? "red" : "#00ab00";
    }

    const buttonDiv = (
      <div
        style={{ display: "flex", alignItems: "center", padding: "0.25rem" }}
      >
        {button}
      </div>
    );

    const rightButton = breakpoints.lg ? buttonDiv : null;
    const bottomButton = breakpoints.lg ? null : buttonDiv;

    playerInfo = (
      <div
        style={{
          display: "flex",
          flexFlow: "column nowrap",
          alignItems: "flex-end",
        }}
      >
        <div style={{ width: "100%" }}>
          <PlayerCard
            player={player}
            backgroundColor={backgroundColor}
            rightSide={rightButton}
            style={{ borderRadius: "1rem" }}
            isAnimated={hasStarted && userHasDraftManagerRole}
          />
        </div>
        {bottomButton}
      </div>
    );
  }

  return <div>{playerInfo}</div>;
}
